import { useState, useEffect } from "react";
import { message } from "antd";
import { FileData, formatToMimeType } from "./constants";

interface Dimension {
    width: number;
    height: number;
}

interface UseFileUploadOptions {
    fileSize: string;
    fileSizeText: string;
    format: string;
    dimensions?: string;
    multiple?: boolean;
    defaultValue?: string | null;
    onChange?: (fileData: FileData[] | null) => void;
}

export function useFileUpload({
	fileSize,
	fileSizeText,
	format,
	dimensions,
	multiple = false,
	defaultValue,
	onChange,
}: UseFileUploadOptions) {
	const [fileList, setFileList] = useState<FileData[]>([]);
	const [uploadProgress, setUploadProgress] = useState(0);

	const maxFileSize = Number(fileSize);

	const allowedFormats = format
		.split(",")
		.map(f => f.trim().toLowerCase())
		.flatMap(f => formatToMimeType[f] || []);

	const allowedExtensions = format
		.split(",")
		.map(f => `.${f.trim().toLowerCase()}`);

	const dimensionList: Dimension[] = dimensions
		? dimensions.split(", ").map(dim => {
			const [width, height] = dim.trim().split("x").map(Number);
			return { width, height };
		})
		: [];

	useEffect(() => {
		if (typeof defaultValue === "string" && defaultValue) {
			const defaultFile: FileData = {
				name: defaultValue.split("/").pop() || "default-image",
				size: 0,
				type: "image/jpeg",
				url: defaultValue,
				file: null,
			};
			setFileList([defaultFile]);
		}
	}, [defaultValue]);

	const validateFile = (file: File): boolean => {
		if (fileList.some(f => f.name === file.name)) {
			message.error(`${file.name} zaten yüklenmiş`);
			return false;
		}

		if (file.size > maxFileSize) {
			message.error(`${file.name} dosyası maksimum boyutu aşıyor (${fileSizeText})`);
			return false;
		}

		if (!allowedFormats.includes(file.type)) {
			message.error(`${file.name} dosya formatı geçersiz. Desteklenen formatlar: ${format}`);
			return false;
		}

		return true;
	};

	const validateImageDimensions = (img: HTMLImageElement, fileName: string): boolean => {
		if (dimensionList.length === 0) return true;

		const isValid = dimensionList.some(
			dim => dim.width === img.width && dim.height === img.height
		);

		if (!isValid) {
			message.error(
				`${fileName} dosyasının boyutları ${dimensionList
					.map(d => `${d.width}x${d.height}`)
					.join(", ")} olmalıdır`
			);
		}

		return isValid;
	};

	const addFile = (fileData: FileData) => {
		if (multiple) {
			setFileList(prevList => {
				const newFileList = [...prevList, fileData];
				if (onChange) onChange(newFileList);
				return newFileList;
			});
		} else {
			message.success(`${fileData.name} dosyası başarıyla yüklendi`);
			setFileList([fileData]);
			if (onChange) onChange([fileData]);
		}
		setUploadProgress(100);
	};

	const beforeUpload = (file: File) => {
		if (!validateFile(file)) return false;

		const isImage = file.type.startsWith("image/");
		const reader = new FileReader();
		reader.readAsDataURL(file);

		reader.onloadstart = () => setUploadProgress(0);
		reader.onprogress = (event) => {
			if (event.lengthComputable) {
				setUploadProgress((event.loaded / event.total) * 100);
			}
		};

		reader.onload = () => {
			const fileData: FileData = {
				name: file.name,
				size: file.size,
				type: file.type,
				url: reader.result,
				file: file,
			};

			if (isImage) {
				const img = new Image();
				img.src = reader.result as string;
				img.onload = () => {
					if (validateImageDimensions(img, file.name)) {
						addFile(fileData);
					}
				};
				img.onerror = () => message.error(`${file.name} görüntü yüklenemedi`);
			} else {
				addFile(fileData);
			}
		};

		return false;
	};

	const handleRemove = (fileNameToRemove: string) => {
		const updatedFileList = fileList.filter(f => f.name !== fileNameToRemove);
		setFileList(updatedFileList);
		message.success("Dosya başarıyla silindi");
		if (onChange) onChange(updatedFileList.length > 0 ? updatedFileList : null);
		setUploadProgress(0);
	};

	const handleRemoveAll = () => {
		setFileList([]);
		if (onChange) onChange(null);
	};

	return {
		fileList,
		uploadProgress,
		allowedExtensions,
		beforeUpload,
		handleRemove,
		handleRemoveAll,
	};
}
